'use client';

/**
 * ESignatureHero — V3 hero for /e-signature.
 *
 * Wraps the shared FeatureHero with e-signature copy and a violet accent,
 * and passes a signature illustration as the right-hand visual. Violet
 * signals "feature page" — same hue used by /services and /business-cards.
 */

import Image from 'next/image';
import React from 'react';
import { Locale } from '../../../../i18n-config';
import FeatureHero from '../_shared/sections/FeatureHero';
import { demoHref } from '../demo/demoInterest';
import { useTranslation } from '@/i18n/client';

type Props = { lang: Locale };

const SIGNATURE_IMAGE = 'https://cdn.1cdn.app/application/UMD/26042921420321_signature.png';

const REGISTER = `${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL}/register`;

const ESignatureHero = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  const ctaPrimary = { label: t('ESignature/Hero/cta_demo'), href: demoHref(lang, 'e-signature') };
  const ctaSecondary = { label: t('ESignature/Hero/cta_signup'), href: REGISTER };

  const visual = (
    <div
      className="relative rounded-3xl p-6 sm:p-8"
      style={{
        background: '#F5EFE0',
        border: '1px solid rgba(255,255,255,0.08)',
      }}
    >
      <div className="relative aspect-square w-full">
        <Image
          src={SIGNATURE_IMAGE}
          alt={t('ESignature/Hero/visual_alt')}
          fill
          sizes="(min-width: 1024px) 420px, 100vw"
          style={{ objectFit: 'contain' }}
          priority
        />
      </div>
    </div>
  );

  return (
    <FeatureHero
      accent="#A78BFA"
      ariaLabel={t('ESignature/Page/aria_hero_section')}
      copy={{
        kicker: t('ESignature/Hero/badge'),
        headlineA: t('ESignature/Hero/heading_part1'),
        headlineB: t('ESignature/Hero/heading_part2'),
        subhead: t('ESignature/Hero/description'),
        ctaPrimary,
        ctaSecondary,
        trust: [t('ESignature/Hero/trust_send'), t('ESignature/Hero/trust_sign'), t('ESignature/Hero/trust2')],
      }}
      visual={visual}
    />
  );
};

export default ESignatureHero;
